/**
 * Batches & expiry: everything on the shelf, by batch, with what it is worth
 * and whether it may be sold. The alerts at the top are the day's work.
 */

import { useState, type ReactNode } from 'react';
import { useAuth } from '../../lib/auth';
import { useWorkingLocation } from '../../lib/locationContext';
import { money } from '../../lib/format';
import { Badge, Button, Card, CardHeader, ErrorState, Input, Modal, Spinner, Textarea } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { usePharmacy, usePharmacyWrite, type BatchRow, type ListOf, type Valuation } from './data';
import { ErrorLine, ExpiryBadge, Table, Td, Tile } from './parts';
import { StockTabs } from './MedicinesPage';
import { AdjustmentModal, type AdjustmentPreset } from './AdjustmentsPage';
import { CountedStockCard } from './CountedStockCard';

interface Alerts {
  expiryAlertDays: number;
  expired: BatchRow[];
  nearExpiry: BatchRow[];
  held: BatchRow[];
  lowStock: Array<{ product_id: string; product_name: string; branch_name: string; reorder_level: number; on_hand: number }>;
  pendingAdjustments: number;
}

export function BatchesPage(): ReactNode {
  const { can, business } = useAuth();
  const { branchId, locations } = useWorkingLocation();
  const c = business?.currencyCode ?? 'NGN';
  const alerts = usePharmacy<Alerts>('/alerts', { branchId });
  const batches = usePharmacy<ListOf<BatchRow>>('/batches', { branchId });
  const valuation = usePharmacy<Valuation>('/valuation', { branchId });
  const [filter, setFilter] = useState('');
  const [quarantine, setQuarantine] = useState<BatchRow | null>(null);
  const [writeOff, setWriteOff] = useState<AdjustmentPreset | null>(null);
  const branchName = (id: string) => locations.find((l) => l.id === id)?.name ?? '—';
  const a = alerts.data;
  const v = valuation.data;
  const rows = (batches.data?.data ?? []).filter((b) =>
    filter ? `${b.productName ?? ''} ${b.batchNumber}`.toLowerCase().includes(filter.toLowerCase()) : true,
  );

  return (
    <div>
      <PageHeader title="Medicines & Stock" description="Every batch on hand, earliest expiry first, with its cost and whether it may be sold" />
      <StockTabs />
      <div className="space-y-5">
        {alerts.isError ? (
          <ErrorState message="Could not load alerts." onRetry={() => void alerts.refetch()} />
        ) : (
          <section aria-label="Alerts" className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Tile label="Expired" value={a?.expired.length ?? '…'} sub="on hand, never sold" tone={a?.expired.length ? 'bad' : 'plain'} />
            <Tile label="Expiring soon" value={a?.nearExpiry.length ?? '…'} sub={`within ${a?.expiryAlertDays ?? '…'} days`} tone={a?.nearExpiry.length ? 'warn' : 'plain'} />
            <Tile label="Held" value={a?.held.length ?? '…'} sub="quarantine or recall" tone={a?.held.length ? 'warn' : 'plain'} />
            <Tile label="Low stock" value={a?.lowStock.length ?? '…'} sub="at or below reorder level" tone={a?.lowStock.length ? 'warn' : 'plain'} />
            <Tile label="Awaiting approval" value={a?.pendingAdjustments ?? '…'} sub="adjustments" />
          </section>
        )}

        {v && (
          <section aria-label="Stock value" className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Tile label="Stock at cost" value={money(v.onShelf, c)} sub={v.uncostedItems ? `${v.uncostedItems} item(s) with no cost recorded` : Number(v.notBatchTracked ?? 0) > 0 ? `incl. ${money(v.notBatchTracked!, c)} not tracked by batch` : 'on the shelf'} />
            <Tile label="Saleable" value={money(v.saleable, c)} tone="good" />
            <Tile label="Held" value={money(v.quarantinedOrRecalled, c)} />
            <Tile label="Expired" value={money(v.expired, c)} tone={Number(v.expired) > 0 ? 'bad' : 'plain'} />
            <Tile label="In transit" value={money(v.inTransit, c)} sub="between locations" />
          </section>
        )}
        {v?.awaitingVerification && (
          <section aria-label="Inventory including awaiting verification" className="grid grid-cols-2 gap-3 lg:grid-cols-5">
            <Tile
              label={v.awaitingVerification.label}
              value={money(v.awaitingVerification.value, c)}
              sub={`${v.awaitingVerification.products} item(s), ${v.awaitingVerification.units} unit(s) · not for sale`}
              tone="warn"
            />
            {v.units && (
                  <Tile
                    label="Units held"
                    value={v.units.recorded.toLocaleString()}
                    sub={`${v.units.onHand.toLocaleString()} with batches or not batch-tracked + ${v.units.countedPending.toLocaleString()} counted, batch/expiry pending`}
                  />
                )}
                <Tile label="Inventory incl. counted stock" value={money(v.totalIncludingAwaiting ?? v.total, c)} sub="stock at cost + counted stock, batch/expiry pending" />
          </section>
        )}

        <CountedStockCard branchId={branchId} currency={c} />

        {a && a.lowStock.length > 0 && (
          <Card padded={false}>
            <div className="p-4 sm:p-5"><CardHeader title="Low stock" /></div>
            <Table head={['Item', 'Location', 'On hand', 'Reorder at']}>
              {a.lowStock.map((l) => (
                <tr key={`${l.product_id}-${l.branch_name}`}>
                  <Td>{l.product_name}</Td>
                  <Td>{l.branch_name}</Td>
                  <Td className="tabular-nums font-medium text-amber-700">{l.on_hand}</Td>
                  <Td className="tabular-nums">{l.reorder_level}</Td>
                </tr>
              ))}
            </Table>
          </Card>
        )}

        <Card padded={false}>
          <div className="p-4 sm:p-5">
            <CardHeader
              title="Batches"
              subtitle="The till chooses batches itself — earliest expiry first, never an expired or held one"
              action={<Input aria-label="Filter batches" placeholder="Filter by item or batch" value={filter} onChange={(e) => setFilter(e.target.value)} />}
            />
          </div>
          {batches.isError ? (
            <div className="p-4"><ErrorState message="Could not load batches." /></div>
          ) : !batches.data ? (
            <Spinner />
          ) : rows.length === 0 ? (
            <p className="px-5 pb-6 text-sm text-slate-500">No batches on hand.</p>
          ) : (
            <Table head={['Item', 'Batch', 'Location', 'Expiry', 'On hand', 'Status', 'Cost / unit', 'Value', '']} minWidth={1000}>
              {rows.map((b) => (
                <tr key={`${b.batchId}-${b.branchId}`} className={b.expired ? 'bg-red-50/40' : undefined}>
                  <Td className="font-medium">{b.productName}</Td>
                  <Td className="font-mono text-xs">{b.batchNumber}</Td>
                  <Td>{branchName(b.branchId)}</Td>
                  <Td><ExpiryBadge expiryDate={b.expiryDate} expired={b.expired} nearExpiry={b.nearExpiry} /></Td>
                  <Td className="tabular-nums">{b.onHand}</Td>
                  <Td>
                    {b.held ? (
                      b.holdKinds.map((k) => <Badge key={k} tone={k === 'recall' ? 'danger' : 'warning'}>{k}</Badge>)
                    ) : b.expired ? (
                      <Badge tone="danger">expired</Badge>
                    ) : (
                      <Badge tone="success">saleable</Badge>
                    )}
                  </Td>
                  <Td className="tabular-nums text-xs">{b.unitCost}</Td>
                  <Td className="tabular-nums">{money(b.value, c)}</Td>
                  <Td className="whitespace-nowrap text-right">
                    {can('pharmacy:stock') && (
                      <>
                        {!b.held && <Button size="sm" variant="ghost" onClick={() => setQuarantine(b)}>Quarantine</Button>}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setWriteOff({
                              product: { id: b.productId, name: b.productName ?? 'Item' },
                              batchId: b.batchId,
                              branchId: b.branchId,
                              quantity: -b.onHand,
                              reason: b.expired ? 'expired' : 'damaged',
                            })
                          }
                        >
                          Write off
                        </Button>
                      </>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
        </Card>
      </div>
      {quarantine && <QuarantineModal batch={quarantine} branchName={branchName(quarantine.branchId)} onClose={() => setQuarantine(null)} />}
      {writeOff && <AdjustmentModal preset={writeOff} onClose={() => setWriteOff(null)} />}
    </div>
  );
}

function QuarantineModal({ batch, branchName, onClose }: { batch: BatchRow; branchName: string; onClose: () => void }): ReactNode {
  const save = usePharmacyWrite<Record<string, unknown>>('post', '/holds');
  const [reason, setReason] = useState('');
  return (
    <Modal open onClose={onClose} title={`Quarantine batch ${batch.batchNumber}`}>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">
          {batch.productName} at {branchName}. It stays on the shelf and in the count, and the till will not sell it until
          somebody who may approve stock releases it.
        </p>
        <Textarea label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            disabled={reason.trim().length < 3}
            loading={save.isPending}
            onClick={() => save.mutate({ batchId: batch.batchId, branchId: batch.branchId, reason: reason.trim() }, { onSuccess: onClose })}
          >
            Quarantine
          </Button>
        </div>
      </div>
    </Modal>
  );
}
